import React from "react";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import VariablesSelectByTypes from "@/components/dashboard/VariablesSelectByTypes";
import Select from "react-select";
import classnames from "classnames";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Form,
  FormGroup,
  Col,
  Label,
  InputGroup,
  Input,
} from "reactstrap";

import HandleContent, { type HandleContentProps } from "./HandleContent";
import VariableField from "../../variableFields/VariableField";
import { resolveContentDeclarations } from "./updatePageHelpers";

// This screen renders only the four basic content types inline; the complex
// types are delegated to HandleContent (both statuses) (same split as
// UpdatePageView — the two lists stay disjoint).
const BASIC_CONTENT_TYPES = ["String", "Number", "Boolean", "Link"];

const contentVarBool = [
  { label: "False", value: false },
  { label: "True", value: true },
];

/**
 * The legacy value channel: String/Number report the raw change event, Boolean
 * the react-select `(option, actionMeta)` pair, so the handler takes either.
 */
type ValueChangeHandler = (eventOrOption: unknown, actionMeta?: unknown) => void;

/** The Link channel: the change event plus the variable it belongs to. */
type LinkVarChangeHandler = (e: React.ChangeEvent<HTMLInputElement>, variableName?: string) => void;

type BasicScalarFieldProps = {
  settingVar: { variable?: string; value?: string | number };
  onVariableValueChange: ValueChangeHandler;
};

type BasicBooleanFieldProps = {
  settingVar: { variable?: string; value?: unknown };
  onVariableValueChange: ValueChangeHandler;
};

type BasicLinkFieldProps = {
  settingVar: { variable?: string; value?: { name?: string; link?: string } };
  onLinkVarChange: LinkVarChangeHandler;
};

// Host-local basic blocks (SCRUM-853): the pinned DOM on this screen is a
// PLAIN value input per type — none of ContentStringField's specials (no
// textarea, no date/authorname cases) — and the legacy channels stay exact:
// String/Number report the raw event (Number persists as a STRING — pinned
// quirk) and Boolean the (option, actionMeta) pair into onVariableValueChange;
// Link reports (event, variableName) into onLinkVarChange.
const BasicNumberField = ({ settingVar, onVariableValueChange }: BasicScalarFieldProps) => (
  <InputGroup className={classnames("input-group-merge")}>
    <Input
      placeholder={`insert value for ${settingVar.variable}`}
      type="number"
      min={0}
      value={settingVar.value}
      onChange={onVariableValueChange}
      name={settingVar.variable}
    />
  </InputGroup>
);

const BasicStringField = ({ settingVar, onVariableValueChange }: BasicScalarFieldProps) => (
  <InputGroup className={classnames("input-group-merge")}>
    <Input
      placeholder={`insert value for ${settingVar.variable}`}
      type="text"
      onChange={onVariableValueChange}
      name={settingVar.variable}
      value={settingVar.value}
    />
  </InputGroup>
);

const BasicBooleanField = ({ settingVar, onVariableValueChange }: BasicBooleanFieldProps) => (
  <Select
    name={settingVar.variable}
    options={contentVarBool}
    onChange={onVariableValueChange}
    value={contentVarBool.filter((ctn) => ctn.value === settingVar.value)}
  />
);

const BasicLinkField = ({ settingVar, onLinkVarChange }: BasicLinkFieldProps) => (
  <FormGroup className="row">
    <Label className="form-control-label" md="2" style={{ color: "#597ade" }}>
      Name
    </Label>
    <Col md="4">
      <Input
        value={settingVar?.value?.name || ""}
        className="form-control-sm"
        placeholder="name"
        type="text"
        name="name"
        onChange={(e) => onLinkVarChange(e, settingVar.variable)}
      />
    </Col>
    <Label className="form-control-label" md="2" style={{ color: "#597ade" }}>
      Link
    </Label>
    <Col md="4">
      <Input
        value={settingVar?.value?.link || ""}
        className="form-control-sm"
        placeholder="link"
        name="link"
        type="text"
        onChange={(e) => onLinkVarChange(e, settingVar.variable)}
      />
    </Col>
  </FormGroup>
);

// STATIC module-level override map (SCRUM-643 hard rule: never close over
// per-render values — the channel handlers travel via fieldProps).
const BASIC_OVERRIDES = {
  Number: BasicNumberField,
  String: BasicStringField,
  Boolean: BasicBooleanField,
  Link: BasicLinkField,
};

/** One content declaration of the page's template. */
type TemplateContentVariable = { _id: string; variable?: string };

/** The page being edited, as UpdatePageHeaderFooterContainer holds it. */
type HeaderFooterPage = {
  page_content?: { variable?: string; variabletype?: string; type?: string; value?: unknown }[];
  page_template?: {
    name?: string;
    content_variables?: {
      ContentRequired: TemplateContentVariable[];
      ContentOptional: TemplateContentVariable[];
    };
  };
};

type TemplateOption = { value?: { name?: string }; label?: string };

type UpdatePageHeaderFooterViewProps = {
  CurrentSite?: unknown;
  page?: HeaderFooterPage;
  selectTemplates: TemplateOption[];
  onTemplateChange: (option: TemplateOption | null) => void;
  onContentRequiredChange: React.ComponentProps<typeof VariablesSelectByTypes>["handleVariableChange"];
  onContentOptionalChange: React.ComponentProps<typeof VariablesSelectByTypes>["handleVariableChange"];
  onVariableValueChange: ValueChangeHandler;
  onLinkVarChange: LinkVarChangeHandler;
  onVariableChange: HandleContentProps["onVariableChange"];
  /** HandleContent (optional)'s sitemap channel: `({ target: { name, value } })`. */
  onChangeContentOptional: (e: { target: { name?: string; value: unknown } }) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  LinkComponent: React.ElementType;
};

export default function UpdatePageHeaderFooterView({
  CurrentSite,
  page,
  selectTemplates,
  onTemplateChange,
  onContentRequiredChange,
  onContentOptionalChange,
  onVariableValueChange,
  onLinkVarChange,
  onVariableChange,
  onChangeContentOptional,
  onSubmit,
  LinkComponent,
}: UpdatePageHeaderFooterViewProps) {
  const selectedTemplate =
    selectTemplates.find((opt) => opt.value?.name === page?.page_template?.name) || null;

  // SCRUM-1508: decide which input renders from the TEMPLATE's declaration, the
  // way UpdatePageContainer already does — not from the page's stored copy.
  // Resolved HERE, in the presentational half, so the container's `page` (which
  // handleSubmit PUTs whole) cannot pick the resolution up: the stored copy is
  // SCRUM-1472 slice 4, not this one. No drifted chrome page exists live today
  // (4 documents, 6 entries, 2026-09-08) — this is the same defect as the
  // pending-pages editor's, guarded before it can surface.
  const resolvedContent = React.useMemo(
    () => resolveContentDeclarations(page?.page_content, page?.page_template),
    [page?.page_content, page?.page_template]
  );
  // A page without drift reaches the embeds as the identical object it always
  // did — `resolveContentDeclarations` returns the same array when it changed
  // nothing, so this returns the same page. The SCRUM-853 prop-identity
  // characterisation keeps holding on its own terms rather than being edited to
  // accommodate a wrapper.
  const resolvedPage = React.useMemo(
    () =>
      resolvedContent === page?.page_content
        ? page
        : { ...page, page_content: resolvedContent },
    [page, resolvedContent]
  );

  return (
    <>
      <SimpleHeader name="Update page" parentName="Pages" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader className="text-center">
            <h3 className="mb-0">Updating Required page</h3>
          </CardHeader>
          <CardBody className="text-center">
            {CurrentSite ? (
              <Form onSubmit={onSubmit}>
                {/* SELECT TEMPLATE */}
                <FormGroup className="row">
                  <Label className="form-control-label" md="2">
                    Design Template
                  </Label>
                  <Col md="10">
                    <Select
                      value={selectedTemplate}
                      name="template"
                      options={selectTemplates}
                      onChange={onTemplateChange}
                      isClearable={true}
                      isSearchable={true}
                      required
                    />
                  </Col>

                  {/* Template Variables Configuration */}
                  {page?.page_template?.content_variables ? (
                    <>
                      <Col md="12" style={{ marginTop: "15px" }}>
                        <center>
                          <h3
                            className="mb-0 text-center"
                            style={{
                              borderBottom: "1px solid #d0d2d6",
                              lineHeight: "0.1em",
                              margin: "10px 0 20px",
                            }}
                          >
                            <span style={{ background: "#fff", padding: "0 10px" }}>
                              Template Variables Configuration
                            </span>
                          </h3>
                        </center>
                      </Col>

                      {/* REQUIRED content variables */}
                      <FormGroup className="col-md-6" style={{ marginTop: "20px" }}>
                        <Col md="10">
                          <VariablesSelectByTypes
                            status="required"
                            type="content"
                            inputLabel="Required"
                            handleVariableChange={onContentRequiredChange}
                            dataValue={page.page_template.content_variables.ContentRequired}
                            show={false}
                          />
                          {resolvedContent
                            .filter((ctn) => ctn.variabletype === "ContentRequired")
                            .map((contentVar) =>
                              BASIC_CONTENT_TYPES.includes(contentVar.type) ? (
                                <VariableField
                                  key={contentVar.variable}
                                  settingVar={contentVar}
                                  options={undefined}
                                  onChange={undefined}
                                  overrides={BASIC_OVERRIDES}
                                  fieldProps={{ onVariableValueChange, onLinkVarChange }}
                                />
                              ) : null
                            )}
                          <HandleContent status="required" page_to_update={resolvedPage} onVariableChange={onVariableChange} />
                        </Col>
                      </FormGroup>

                      {/* OPTIONAL content variables */}
                      <FormGroup className="col-md-6" style={{ marginTop: "20px" }}>
                        <Col md="10">
                          <VariablesSelectByTypes
                            status="optional"
                            type="content"
                            inputLabel="Optional"
                            handleVariableChange={onContentOptionalChange}
                            dataValue={page.page_template.content_variables.ContentOptional}
                            show={false}
                          />
                          {resolvedContent
                            .filter((ctn) => ctn.variabletype === "ContentOptional")
                            .map((contentVar) =>
                              BASIC_CONTENT_TYPES.includes(contentVar.type) ? (
                                <VariableField
                                  key={contentVar.variable}
                                  settingVar={contentVar}
                                  options={undefined}
                                  onChange={undefined}
                                  overrides={BASIC_OVERRIDES}
                                  fieldProps={{ onVariableValueChange, onLinkVarChange }}
                                />
                              ) : null
                            )}
                          <HandleContent status="optional" page_to_update={resolvedPage} onChangeContentOptional={onChangeContentOptional} onVariableChange={onVariableChange} />
                        </Col>
                      </FormGroup>
                    </>
                  ) : null}
                </FormGroup>

                {/* Actions */}
                <button className="btn btn-outline-primary mr-1 float-right" type="submit">
                  Finish Updating
                </button>
                <LinkComponent to="/admin/display-pages" className="btn btn-outline-default mr-1 float-left">
                  Back To Pages
                </LinkComponent>
              </Form>
            ) : (
              <LinkComponent to="/admin/display-pages" className="btn btn-outline-default mr-1 float-left">
                Back To Pages
              </LinkComponent>
            )}
          </CardBody>
        </Card>
      </Container>
    </>
  );
}
